import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { bulkDeleteUsers } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grow from '@mui/material/Grow';
import Typography from '@mui/material/Typography';

interface DeleteUsersDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedUsers: string[];
  setSelectedUsers: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function DeleteUsersDialog({
  open,
  handleClose,
  selectedUsers,
  setSelectedUsers,
  changeLogRoles,
}: DeleteUsersDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [newEmailCheckBox, setNewEmailCheckBox] = useState(false);
  const [requiredBoxes, setRequiredBoxes] = useState({
    documents: false,
    emails: false,
    calendarEvents: false,
  });
  const { documents, emails } = requiredBoxes;

  const { mutate, isPending: isDeleting } = useMutation({
    mutationFn: () =>
      bulkDeleteUsers(
        'User',
        selectedUsers,
        newEmailCheckBox,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to start deleting the users has started.',
      });
      setChangeLogMessage('');
      setNewEmailCheckBox(false);
      setRequiredBoxes({
        documents: false,
        emails: false,
        calendarEvents: false,
      });
      setSelectedUsers([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting the selected user(s). Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_grid.bulk_delete_users, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.name,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, UserRoles.BulkDeleteUsers.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRequiredBoxes({
      ...requiredBoxes,
      [event.target.name]: event.target.checked,
    });
  };

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setNewEmailCheckBox(false);
        setRequiredBoxes({
          documents: false,
          emails: false,
          calendarEvents: false,
        });
      }}
      title={selectedUsers.length > 1 ? 'Delete Users' : 'Delete User'}
      dialogContent={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography>
            {selectedUsers.length > 1 ? (
              <>
                Delete <strong>{selectedUsers.length} users</strong>?
              </>
            ) : (
              <>
                Delete <strong>{selectedUsers[0]}</strong>?
              </>
            )}
          </Typography>

          <Box>
            <FormGroup sx={{ ml: 2 }}>
              <FormControlLabel
                sx={{ width: 'fit-content' }}
                control={
                  <Checkbox
                    checked={newEmailCheckBox}
                    onChange={() => {
                      setNewEmailCheckBox(!newEmailCheckBox);
                    }}
                  />
                }
                label={
                  <Typography>
                    {selectedUsers.length > 1
                      ? 'Make usernames immediately reusable.'
                      : 'Make username immediately reusable.'}
                  </Typography>
                }
              />
            </FormGroup>
            {newEmailCheckBox && (
              <Grow in={newEmailCheckBox}>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Normally, a username cannot be reused immediately after being
                  deleted. Google prevents the reuse of the address while they
                  are busy deleting all your files. If you allow gPanel to
                  rename the account before you delete it, accounts can be
                  immediately used elsewhere (as a group, user or alias email).
                  Please note that recreating a new user account too soon can
                  result in the incomplete deletion of the user's documents,
                  calendars, sites and other files.
                </Alert>
              </Grow>
            )}
          </Box>

          <Divider sx={{ mx: -3 }} />

          <Box>
            <Typography sx={{ mb: 2 }}>
              To proceed, please acknowledge the following:
            </Typography>

            <FormControl component="fieldset">
              <FormGroup sx={{ ml: 2, gap: 1 }}>
                <FormControlLabel
                  sx={{ width: 'fit-content' }}
                  control={
                    <Checkbox
                      checked={documents}
                      onChange={handleChange}
                      name="documents"
                    />
                  }
                  label={
                    selectedUsers.length > 1 ? (
                      <Typography>
                        I understand that all <strong>documents</strong> owned
                        by these users will be inaccessible.
                      </Typography>
                    ) : (
                      <Typography>
                        I understand that all <strong>documents</strong> owned
                        by this user will be inaccessible.
                      </Typography>
                    )
                  }
                />
                <FormControlLabel
                  sx={{ width: 'fit-content' }}
                  control={
                    <Checkbox
                      checked={emails}
                      onChange={handleChange}
                      name="emails"
                    />
                  }
                  label={
                    <Typography>
                      I understand that all <strong>emails</strong> and{' '}
                      <strong>calendar events</strong> will be permanently
                      deleted.
                    </Typography>
                  }
                />
              </FormGroup>
            </FormControl>
          </Box>

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isDeleting}
          />
        </Box>
      }
      primaryButton={{
        title: selectedUsers.length > 1 ? 'Delete Users' : 'Delete User',
        color: 'error',
        action: handleSubmit,
        loading: isDeleting,
        disabled:
          isDeleting ||
          requiredBoxes.documents === false ||
          requiredBoxes.emails === false,
      }}
    />
  );
}
